import { QueryKeys } from "@/constants/Query";
import type { PatientTabsResponse } from "@/features/PatientTabs/network/usePatientTabs";
import SocketManager from "@/lib/socketManager";
import { socketsAtom } from "@/store/socketsAtom";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";
import type {
  ChatParticipant,
  UniversalChatMessage,
  UniversalChatPage,
  UniversalChatQueryData,
  UpdateRoomMetadataMessage,
  WebSocketMessageData,
} from "../types/universalChatTypes";

/**
 * Handles global socket events for all universal clinic chat rooms.
 * This hook listens to socket messages for all rooms and updates their caches accordingly.
 * Should be mounted once at the app root level (in __root.tsx) to ensure all chat data
 * stays in sync regardless of which room the user is viewing.
 *
 * Handles: chat messages, room metadata updates, and refresh triggers.
 */
export function useUniversalChatSocketGlobalHandler() {
  const queryClient = useQueryClient();
  const socket = useAtomValue(socketsAtom)?.["universal-clinic-chat"];

  const handleTriggerRefresh = useCallback(() => {
    if (!socket) return;
    socket.emit("refresh");
  }, [socket]);

  const handleUpdateRoomMetadata = useCallback(
    (message: UpdateRoomMetadataMessage) => {
      queryClient.setQueryData<PatientTabsResponse>(
        QueryKeys.Provider.PATIENT_TABS,
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            patientTabs: oldData.patientTabs.map((tab) => {
              // If we find a tab match, update patientWaitingSince
              if (tab.patient.id === message.patientId) {
                return {
                  ...tab,
                  sla: {
                    patientWaitingSince: message.customerWaitingSince,
                  },
                };
              }
              // Otherwise, return the tab unchanged
              return tab;
            }),
          };
        },
      );
    },
    [queryClient],
  );

  const handleNewMessage = useCallback(
    (messageData: WebSocketMessageData) => {
      const roomId = messageData.roomId;

      // Ignore messages without a roomId
      if (!roomId) return;

      // Normalize the message for cache storage
      const newMessage: UniversalChatMessage = {
        ...messageData,
        roomId,
      } as UniversalChatMessage;

      // Update the cache for the room, regardless of whether it's currently being viewed
      queryClient.setQueryData(
        QueryKeys.Patient.Chat.Messages.UNIVERSAL_CLINIC(roomId),
        (oldData: UniversalChatQueryData | undefined) => {
          if (!oldData) return oldData;

          const isExistingMessage = (existingMessage: UniversalChatMessage) => {
            const clientIdsMatch =
              existingMessage.clientId === newMessage.clientId;
            return clientIdsMatch;
          };

          // Check if we need to add a new participant, we do this
          // separately from the check for the existing message because we optimistically
          // add the message to our query cache when we send it but we don't
          // have participant data at that point, here we incorporate the
          // participant from the server's message if and when we get it
          let updatedParticipants = oldData.pages[0]?.data.participants || [];

          const hasNewParticipant =
            messageData.participant &&
            !updatedParticipants.find(
              (p: ChatParticipant) => p.id === messageData.participant?.id,
            );

          if (hasNewParticipant && messageData.participant) {
            updatedParticipants = [
              ...updatedParticipants,
              messageData.participant,
            ];
          }

          // First pass: attempt to merge into existing message(s)
          let foundExisting = false;
          const pagesWithMergedMessages = oldData.pages.map(
            (page: UniversalChatPage) => {
              const mergedMessages = page.data.messages.map(
                (existingMessage: UniversalChatMessage) => {
                  if (isExistingMessage(existingMessage)) {
                    foundExisting = true;
                    // If we have new participant data, do not mutate existing message;
                    // only update participants collection (already handled above).
                    if (hasNewParticipant) {
                      return existingMessage;
                    }
                    // Otherwise, merge incoming over existing to capture server-side updates
                    return {
                      ...existingMessage,
                      ...newMessage,
                    } as UniversalChatMessage;
                  }
                  return existingMessage;
                },
              );
              return {
                ...page,
                data: {
                  ...page.data,
                  messages: mergedMessages,
                  participants: updatedParticipants,
                },
              };
            },
          );

          // If not found anywhere, add to top of first page (most recent)
          const updatedPages = foundExisting
            ? pagesWithMergedMessages
            : pagesWithMergedMessages.map(
                (page: UniversalChatPage, index: number) => {
                  if (index === 0) {
                    return {
                      ...page,
                      data: {
                        ...page.data,
                        messages: [newMessage, ...page.data.messages],
                        participants: updatedParticipants,
                      },
                    };
                  }
                  return page;
                },
              );

          return {
            ...oldData,
            pages: updatedPages,
            participants: updatedParticipants,
          };
        },
      );
    },
    [queryClient],
  );

  useEffect(() => {
    if (!socket) return;

    socket.on("trigger_refresh", handleTriggerRefresh);
    socket.on("update_room_metadata", handleUpdateRoomMetadata);
    socket.on("chat_message", handleNewMessage);

    return () => {
      socket.off("trigger_refresh", handleTriggerRefresh);
      socket.off("update_room_metadata", handleUpdateRoomMetadata);
      socket.off("chat_message", handleNewMessage);
    };
  }, [
    socket,
    handleTriggerRefresh,
    handleUpdateRoomMetadata,
    handleNewMessage,
  ]);
}

/**
 * Handles room-specific functionality for a single universal clinic chat room.
 * This hook manages sending messages and optimistic updates for the specified room only.
 * Use this hook in components that display a specific universal clinic chat room.
 *
 * Note: Message receiving for all rooms is handled globally by useUniversalChatSocketGlobalHandler.
 */
export function useUniversalChatSocketRoom(roomId: string) {
  const queryClient = useQueryClient();

  const sendMessage = useCallback(
    (message: WebSocketMessageData) => {
      const socket = SocketManager.get("universal-clinic-chat");
      if (!socket) return;

      // Emit the message to the server
      socket.emit("chat_message", message);

      // Optimistically add the message to the cache
      queryClient.setQueryData(
        QueryKeys.Patient.Chat.Messages.UNIVERSAL_CLINIC(roomId),
        (oldData: UniversalChatQueryData | undefined) => {
          if (!oldData) return oldData;

          const newMessage: UniversalChatMessage = {
            ...message,
            roomId,
          } as UniversalChatMessage;

          const updatedPages = oldData.pages.map(
            (page: UniversalChatPage, index: number) => {
              if (index === 0) {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    messages: [newMessage, ...page.data.messages],
                  },
                };
              }
              return page;
            },
          );

          return {
            ...oldData,
            pages: updatedPages,
          };
        },
      );
    },
    [queryClient, roomId],
  );

  return {
    sendMessage,
  };
}
